import {
  Tabs as AriaTabs,
  Tab as AriaTab,
  TabList as AriaTabList,
  TabPanel as AriaTabPanel,
} from 'react-aria-components';

import { cx } from '../../utils/cx';

import type {
  TabsProps as AriaTabsProps,
  TabProps as AriaTabProps,
  TabListProps as AriaTabListProps,
  TabPanelProps as AriaTabPanelProps,
} from 'react-aria-components';

const TabsRoot = (props: AriaTabsProps) => {
  return (
    <AriaTabs
      {...props}
      className={(state) =>
        cx('flex flex-col', typeof props.className === 'function' ? props.className(state) : props.className)
      }
    />
  );
};

const TabList = <T extends object>(props: AriaTabListProps<T>) => {
  return (
    <AriaTabList
      {...props}
      className={(state) =>
        cx(
          // Scrolls horizontally (scrollbar hidden) so overflowing tabs stay
          // reachable on narrow viewports instead of clipping past the edge.
          'border-secondary scrollbar-hide flex gap-5 overflow-x-auto border-b',
          typeof props.className === 'function' ? props.className(state) : props.className
        )
      }
    />
  );
};

const Tab = (props: AriaTabProps) => {
  return (
    <AriaTab
      {...props}
      className={(state) =>
        cx(
          // Active underline inherits the active text color via currentColor — no
          // dedicated border-fg-primary token, and it flips automatically in dark mode.
          'outline-focus-ring text-md flex h-9 cursor-pointer items-center justify-center whitespace-nowrap border-b-2 border-transparent px-1 pb-3 font-semibold transition-colors duration-100 ease-linear',
          state.isSelected ? 'text-primary border-[currentColor]' : 'text-quaternary',
          !state.isSelected && (state.isHovered || state.isFocused) && 'text-primary',
          state.isFocusVisible && 'outline-2 outline-offset-2',
          state.isDisabled && 'cursor-not-allowed opacity-50',
          typeof props.className === 'function' ? props.className(state) : props.className
        )
      }
    />
  );
};

const TabPanel = (props: AriaTabPanelProps) => {
  return (
    <AriaTabPanel
      {...props}
      className={(state) =>
        cx('outline-focus-ring', typeof props.className === 'function' ? props.className(state) : props.className)
      }
    />
  );
};

/**
 * Horizontal tab navigation. Thin wrapper over `react-aria-components` Tabs
 * (keyboard nav + a11y for free) styled to the Mantle design system. The tab
 * list scrolls horizontally when the tabs overflow its width.
 *
 * Pair `Tabs.Tab` `id`s with `Tabs.Panel` `id`s; selection is controlled by
 * react-aria via `defaultSelectedKey` / `selectedKey` on `Tabs.Root`.
 */
const Tabs = {
  Root: TabsRoot,
  List: TabList,
  Tab,
  Panel: TabPanel,
};

export default Tabs;
